import React, { useState } from "react"
import styled from "styled-components"
import { useHistory } from "react-router-dom"
import { stringify } from "qs"

import { URLOptions } from "types"
import Notice from "components/Notice"
import { NoticeContainer } from "components/NoticeContainer"

const Container = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  flex-direction: column;
  padding: 2em;
  justify-content: space-between;
  background: #000000;
  color: #ffffff;
  font-family: 'Arial';
  font-weight: 300;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`

const Text = styled.div`
  margin: 0;
  font-size: 12pt;
  font-weight: 300;
  line-height: 1.5;
  box-sizing: border-box;
  color: #ffffff;
`

const Logo = styled.div`
  margin: 0 0 1em 0;
  line-height: 1;
  
  img {
    width: auto;
    height: 79px;  // Original height
    
    @media (max-width: 768px) {
      height: 60px;  // Scaled down ~25% for mobile but maintaining proportion
    }
  }
`

const Instructions = styled.div`
  margin: 2em 0;
  color: #ffffff;
`

const Form = styled.form`
  display: inline;
  line-height: 1.35;
`

const Options = styled.div`
  padding: 0.15em 1em 0.15em;
`

const Option = styled.div`
  display: flex;
  align-items: center;
  color: #ffffff;
`

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  margin-right: 1em;
  height: 2em;
  width: 2em;
`

const Input = styled.input`
  font-size: 12pt;
  width: 10em;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
  line-height: 1.25;
  height: 2em;
  background: transparent;
  color: #ffffff;
  padding: 4px 0;
  margin: 4px 0;
  font-family: 'Arial';
  font-weight: 300;
  -webkit-appearance: none;
  border-radius: 0;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-bottom-color: rgba(255, 255, 255, 1);
  }
  
  /* Mobile specific adjustments */
  @media (max-width: 768px) {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    max-width: 20em;
  }
`

const Top = styled.div``

const Bottom = styled(Text as any)`
  line-height: 1.35;
  font-size: 12pt;
  max-width: 40em;
  
  a {
    color: #ffffff;
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    
    &:hover {
      border-bottom-color: rgba(255, 255, 255, 1);
    }
  }
`

const Button = styled.button`
  font-size: 12pt;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 1.0);
  color: #ffffff;
  padding: 4px 8px;
  transition: all 0.2s ease;
  border-radius: 6px;
  font-weight: 300;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
  }
`

const Home: React.FC = ({ ...props }) => {
  const history = useHistory()
  const [url, setUrl] = useState<string | null>("https://www.are.na/share/TWGuZhK")
  const [options, setOptions] = useState<URLOptions>({
    author: true,
    source: true,
    description: true,
    toc: true,
    isShare: false,
    reverse: true,
  })

  const onOptionChange = (
    key: "author" | "source" | "description" | "toc" | "reverse",
    value: boolean
  ) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      [key]: value,
    }))
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const splitURL = url && url.split("/")

    console.log("splitURL", splitURL)

    if (
      (splitURL && splitURL.length !== 5) ||
      (splitURL && splitURL[2] !== "are.na" && splitURL[2] !== "www.are.na")
    ) {
      return history.push(`/error/not_a_channel`)
    }

    if (splitURL && splitURL[3] === "share") {
      console.log("is_share")
      options["isShare"] = true
    }

    const slug = splitURL && splitURL.pop()

    if (!slug) return false

    console.log({ slug, options })

    history.push(`/book/${slug}?${stringify(options)}`)
  }

  return (
    <Container>
      <Top>
       <Logo>
          <img src="/logo.svg" alt="mcluhan.are.na" />
        </Logo>
        <Form onSubmit={onSubmit}>
          <Instructions>
            <Text>
              1. Enter the URL of a public Are.na channel:{" "}
              <Input
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setUrl(e.target.value)
                }
                type="text"
                name="url"
                placeholder=""
              />
              <Text>2. (Optional) Choose your settings:</Text>
              <Options>
                <Option>
                  <Checkbox
                    checked={options.toc}
                    onChange={e => onOptionChange("toc", !options.toc)}
                  />
                  <label>include table of contents</label>
                </Option>
                <Option>
                  <Checkbox
                    checked={options.author}
                    onChange={e => onOptionChange("author", !options.author)}
                  />
                  <label>display author</label>
                </Option>
                <Option>
                  <Checkbox
                    checked={options.source}
                    onChange={e => onOptionChange("source", !options.source)}
                  />
                  <label>display source</label>
                </Option>
                <Option>
                  <Checkbox
                    checked={options.description}
                    onChange={e =>
                      onOptionChange("description", !options.description)
                    }
                  />
                  <label>display description</label>
                </Option>
                <Option>
                  <Checkbox
                    checked={options.reverse}
                    onChange={e => onOptionChange("reverse", !options.reverse)}
                  />
                  <label>reverse chronological order</label>
                </Option>
              </Options>
              <br />
              <Text>
                <Button>Make book</Button>
              </Text>
            </Text>
          </Instructions>
        </Form>
      </Top>

      <Bottom>
        mcluhan.are.na is a fork of print.are.na by <a href="http://moonshot.ooo">Duane King</a> for{" "} 
        <a href="https://baukunst.co">Baukunst</a> Study Group 003, Understanding Media with <a href="https://themcluhaninstitute.com">The McLuhan Institute</a>.{" "}
        <a href="https://print.are.na">print.are.na</a> was created by <a href="https://mindyseu.com">Mindy Seu</a>,{" "}
        <a href="https://www.charlesbroskoski.com">Charles Broskoski</a> and{" "}
        <a href="https://studioijeoma.com">Ekene Ijeoma</a>, and uses{" "}
        <a href="https://bindery.info">bindery.js</a>, an open-source library for
        creating books using HTML and CSS, created by{" "}
        <a href="https://evanbrooks.info">Evan Brooks</a>. The first version{" "}
        <a href="https://github.com/GeneralTrademark/print-arena">
          print.are.na
        </a>{" "}
        was created by <a href="https://callil.com">Callil Capuozzo</a> for the
        2017 Cybernetics Conference.
      </Bottom>
      <NoticeContainer>
      <Notice id="demo">
          <strong>Note:</strong> First time visitor? Watch this{" "}
          <a href="https://youtu.be/t1axnba_Ueg?si=sWBgRWicKF7_UvZ6">educational video</a>.
        </Notice>
      </NoticeContainer>
    </Container>
  )
}

export default Home
